import React, { useState } from 'react';
import { FileText, ChevronDown, ChevronUp, Shield, BookOpen, Users, Award, CreditCard, AlertTriangle, Mail } from 'lucide-react';

const Section = ({ id, icon: Icon, title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="border border-gray-200 rounded-xl mb-4 overflow-hidden" id={id}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 bg-white hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-primary flex-shrink-0" />}
          <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>
      {open && (
        <div className="p-5 pt-0 bg-white border-t border-gray-100 text-gray-600 leading-relaxed space-y-3">
          {children}
        </div>
      )}
    </div>
  );
};

const TermsOfUse = () => {
  const lastUpdate = '30 avril 2026';

  const toc = [
    { id: 'acceptation', label: 'Acceptation des conditions' },
    { id: 'plateforme', label: 'Description de la plateforme' },
    { id: 'inscription', label: 'Inscription et compte utilisateur' },
    { id: 'utilisation', label: 'Utilisation autorisée' },
    { id: 'cours', label: 'Cours, certifications et contenu' },
    { id: 'paiements', label: 'Abonnements et paiements' },
    { id: 'propriete', label: 'Propriété intellectuelle' },
    { id: 'conduite', label: 'Règles de conduite communautaire' },
    { id: 'responsabilite', label: 'Limitation de responsabilité' },
    { id: 'resiliation', label: 'Résiliation du compte' },
    { id: 'modifications', label: 'Modifications des conditions' },
    { id: 'droit', label: 'Droit applicable et litiges' },
    { id: 'contact', label: 'Contact' },
  ];

  return (
    <div className="min-h-full bg-gray-50">
      {/* Hero */}
      <div className="bg-gradient-to-r from-primary to-secondary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center gap-4 mb-4">
            <FileText className="w-10 h-10 opacity-80" />
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">Conditions d'utilisation</h1>
              <p className="text-white/80 mt-1">Dernière mise à jour : {lastUpdate}</p>
            </div>
          </div>
          <p className="text-lg text-white/90 max-w-2xl">
            Bienvenue sur Innovactive360°. En accédant à notre plateforme, vous acceptez les présentes conditions.
            Veuillez les lire attentivement avant d'utiliser nos services.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar TOC */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="bg-white border border-gray-200 rounded-xl p-4 sticky top-24">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wide">Table des matières</h3>
              <nav className="space-y-1">
                {toc.map(item => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    className="block text-sm text-gray-600 hover:text-primary py-1 px-2 rounded hover:bg-primary/5 transition-colors"
                  >
                    {item.label}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <main className="flex-1 min-w-0">
            <Section id="acceptation" icon={Shield} title="1. Acceptation des conditions">
              <p>
                Les présentes Conditions d'utilisation (ci-après « Conditions ») régissent l'accès et
                l'utilisation de la plateforme Innovactive360° (ci-après « la Plateforme »), exploitée par
                la société <strong>Innovactive360°</strong>, dont le siège social est situé à Parakou, Bénin.
              </p>
              <p>
                En vous inscrivant, en accédant ou en utilisant la Plateforme, vous reconnaissez avoir lu,
                compris et accepté sans réserve les présentes Conditions, ainsi que notre{' '}
                <a href="/privacy" className="text-primary hover:underline">Politique de confidentialité</a>.
              </p>
              <p>
                Si vous êtes mineur(e) ou si vous n'avez pas la capacité juridique requise pour conclure un
                contrat, vous devez obtenir le consentement de votre représentant légal avant d'utiliser
                la Plateforme.
              </p>
              <p>
                L'utilisation de la Plateforme par une personne morale implique que le représentant légal
                accepte les présentes Conditions au nom de l'entité.
              </p>
            </Section>

            <Section id="plateforme" icon={BookOpen} title="2. Description de la plateforme">
              <p>
                Innovactive360° est une plateforme SaaS unifiée dédiée à l'entrepreneuriat, à la formation
                en ligne et à l'innovation. Elle propose notamment les services suivants :
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <strong>Formation en ligne (MOOC) :</strong> accès à des cours structurés en modules et
                  leçons, avec suivi de progression, évaluations/quiz et délivrance de certificats de
                  complétion vérifiables.
                </li>
                <li>
                  <strong>Coaching et mentorat :</strong> plans de coaching personnalisés, sessions de
                  suivi et indicateurs de performance.
                </li>
                <li>
                  <strong>Gamification :</strong> système de points d'expérience (XP), niveaux, badges,
                  classements et séries quotidiennes pour stimuler l'engagement.
                </li>
                <li>
                  <strong>Communauté :</strong> espaces de discussion par cours, messagerie et
                  interactions entre apprenants et instructeurs.
                </li>
                <li>
                  <strong>Bourse d'idées :</strong> outil permettant aux entrepreneurs de soumettre et
                  valoriser des projets auprès d'investisseurs.
                </li>
                <li>
                  <strong>Plans d'affaires :</strong> module de création et de gestion de business plans.
                </li>
                <li>
                  <strong>Assistant IA :</strong> assistant conversationnel basé sur l'intelligence
                  artificielle pour accompagner les apprenants.
                </li>
                <li>
                  <strong>Marketplace :</strong> espace d'achat et de vente de contenus et services liés
                  à l'entrepreneuriat.
                </li>
              </ul>
              <p>
                Innovactive360° se réserve le droit de modifier, suspendre ou supprimer tout ou partie de
                ces services à tout moment, avec ou sans préavis.
              </p>
            </Section>

            <Section id="inscription" icon={Users} title="3. Inscription et compte utilisateur">
              <p>
                L'accès aux fonctionnalités complètes de la Plateforme requiert la création d'un compte.
                Lors de l'inscription, vous vous engagez à :
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Fournir des informations exactes, complètes et à jour (nom, prénom, adresse e-mail, etc.).</li>
                <li>Choisir un mot de passe robuste et le maintenir confidentiel.</li>
                <li>Ne pas créer plus d'un compte à titre personnel sauf autorisation expresse.</li>
                <li>Ne pas utiliser le compte d'un tiers sans son autorisation explicite.</li>
                <li>Notifier immédiatement Innovactive360° de tout accès non autorisé à votre compte.</li>
              </ul>
              <p>
                Vous êtes seul(e) responsable de toutes les activités effectuées sous votre compte.
                Innovactive360° ne pourra être tenu responsable de tout dommage résultant d'un manquement
                à ces obligations.
              </p>
              <p>
                La Plateforme propose les rôles suivants : <strong>Apprenant, Instructeur, Entrepreneur,
                Investisseur et Administrateur</strong>. Les droits et responsabilités varient selon le
                rôle attribué.
              </p>
            </Section>

            <Section id="utilisation" icon={Shield} title="4. Utilisation autorisée">
              <p>Vous vous engagez à utiliser la Plateforme uniquement à des fins licites. Il est notamment interdit de :</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Publier ou diffuser du contenu illégal, frauduleux, diffamatoire, obscène ou portant atteinte aux droits d'autrui.</li>
                <li>Tenter d'accéder sans autorisation aux systèmes informatiques, bases de données ou comptes d'autres utilisateurs.</li>
                <li>Introduire des virus, malwares, spywares ou tout autre code malveillant.</li>
                <li>Utiliser des robots, scripts ou tout procédé automatisé pour extraire des données de la Plateforme (scraping) sans autorisation écrite.</li>
                <li>Vendre, louer, transférer ou sous-licencier votre accès ou votre compte à un tiers.</li>
                <li>Usurper l'identité d'une autre personne ou d'une entité.</li>
                <li>Contourner, désactiver ou interférer avec les mécanismes de sécurité de la Plateforme.</li>
                <li>Utiliser la Plateforme à des fins concurrentielles sans autorisation préalable.</li>
              </ul>
              <p>
                Tout manquement à ces règles peut entraîner la suspension ou la résiliation immédiate de
                votre compte, sans préjudice de poursuites judiciaires éventuelles.
              </p>
            </Section>

            <Section id="cours" icon={Award} title="5. Cours, certifications et contenu">
              <h3 className="font-semibold text-gray-700 mt-2">5.1 Accès aux cours</h3>
              <p>
                L'accès aux cours peut être gratuit ou soumis à un abonnement payant selon le plan
                souscrit. Innovactive360° ne garantit pas la disponibilité permanente d'un cours
                spécifique et peut le retirer à tout moment.
              </p>
              <h3 className="font-semibold text-gray-700 mt-4">5.2 Certificats de complétion</h3>
              <p>
                Un certificat numérique vérifiable est automatiquement délivré à l'issue de la
                complétion d'un cours. Ces certificats attestent de la réalisation du parcours sur la
                Plateforme et ne constituent pas une certification académique ou professionnelle
                officielle reconnue par un organisme gouvernemental, sauf mention contraire explicite.
              </p>
              <p>
                Les certificats sont vérifiables à l'adresse{' '}
                <a href="/verify" className="text-primary hover:underline">app.mooc.innovactive360.cloud/verify</a>.
              </p>
              <h3 className="font-semibold text-gray-700 mt-4">5.3 Contenu généré par les utilisateurs</h3>
              <p>
                En publiant du contenu sur la Plateforme (avis, messages, cours en tant qu'instructeur,
                etc.), vous accordez à Innovactive360° une licence mondiale, non exclusive, gratuite et
                transférable pour utiliser, reproduire, modifier, distribuer et afficher ce contenu
                dans le cadre de l'exploitation de la Plateforme.
              </p>
              <p>
                Vous déclarez et garantissez que vous disposez de tous les droits nécessaires sur le
                contenu que vous publiez et qu'il ne viole aucun droit de tiers.
              </p>
              <h3 className="font-semibold text-gray-700 mt-4">5.4 Évaluations et quiz</h3>
              <p>
                Les résultats d'évaluations et de quiz sont enregistrés à des fins pédagogiques et de
                suivi de progression. Innovactive360° ne garantit pas un score minimal ou un résultat
                spécifique.
              </p>
            </Section>

            <Section id="paiements" icon={CreditCard} title="6. Abonnements et paiements">
              <h3 className="font-semibold text-gray-700 mt-2">6.1 Plans tarifaires</h3>
              <p>
                La Plateforme propose plusieurs formules d'abonnement avec des niveaux d'accès
                différents. Les tarifs en vigueur sont affichés sur la page{' '}
                <a href="/subscription" className="text-primary hover:underline">Abonnements</a>.
                Innovactive360° se réserve le droit de modifier ses tarifs avec un préavis de 30 jours.
              </p>
              <h3 className="font-semibold text-gray-700 mt-4">6.2 Facturation</h3>
              <p>
                Les abonnements sont facturés selon la périodicité choisie (mensuelle ou annuelle).
                Le renouvellement est automatique sauf résiliation avant la date d'échéance.
              </p>
              <h3 className="font-semibold text-gray-700 mt-4">6.3 Politique de remboursement</h3>
              <p>
                Toute demande de remboursement doit être soumise dans un délai de 14 jours suivant
                l'achat, sous réserve que moins de 20% du contenu acheté n'ait été consulté.
                Passé ce délai ou ce seuil d'utilisation, aucun remboursement ne sera accordé.
                Les demandes sont à adresser à{' '}
                <a href="mailto:support@innovactive360.com" className="text-primary hover:underline">
                  support@innovactive360.com
                </a>.
              </p>
              <h3 className="font-semibold text-gray-700 mt-4">6.4 Défaut de paiement</h3>
              <p>
                En cas de défaut de paiement, l'accès aux fonctionnalités payantes sera suspendu.
                Vos données et votre progression seront conservées pendant 90 jours, délai après
                lequel elles pourront être supprimées définitivement.
              </p>
            </Section>

            <Section id="propriete" icon={Shield} title="7. Propriété intellectuelle">
              <p>
                L'ensemble des éléments constituant la Plateforme — logiciels, interfaces, textes,
                images, vidéos, logos, marques, algorithmes, bases de données — sont la propriété
                exclusive d'Innovactive360° ou de ses concédants de licence et sont protégés par les
                lois applicables en matière de propriété intellectuelle.
              </p>
              <p>
                Toute reproduction, représentation, modification, distribution ou exploitation
                non autorisée de ces éléments est strictement interdite et constitue une contrefaçon
                susceptible d'engager la responsabilité civile et pénale de son auteur.
              </p>
              <p>
                Le nom, le logo et les marques « Innovactive360° » sont des marques déposées.
                Aucune disposition des présentes Conditions ne vous confère de droit d'utiliser ces
                marques sans l'autorisation écrite préalable d'Innovactive360°.
              </p>
            </Section>

            <Section id="conduite" icon={Users} title="8. Règles de conduite communautaire">
              <p>
                Innovactive360° aspire à offrir un environnement d'apprentissage sûr, respectueux et
                inclusif. À ce titre, les comportements suivants sont formellement interdits dans
                les espaces de discussion, avis, messagerie et tout autre espace communautaire :
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Harcèlement, intimidation, menaces ou discours haineux fondés sur l'origine, le genre, la religion, l'orientation sexuelle ou tout autre critère.</li>
                <li>Spam, publicité non sollicitée ou démarchage commercial auprès d'autres utilisateurs.</li>
                <li>Publication d'informations personnelles d'autres utilisateurs sans leur consentement (doxxing).</li>
                <li>Avis, notations ou commentaires frauduleux ou manipulés.</li>
                <li>Toute forme de triche lors des évaluations (partage de réponses, usurpation d'identité, etc.).</li>
              </ul>
              <p>
                Innovactive360° se réserve le droit de modérer, supprimer ou modifier tout contenu
                jugé contraire à ces règles, sans préavis ni indemnité.
              </p>
            </Section>

            <Section id="responsabilite" icon={AlertTriangle} title="9. Limitation de responsabilité">
              <p>
                La Plateforme est fournie « en l'état » et « selon disponibilité ». Innovactive360°
                s'efforce d'assurer la continuité du service mais ne peut garantir une disponibilité
                ininterrompue, notamment en cas de maintenance, de force majeure ou de défaillance
                des prestataires techniques.
              </p>
              <p>
                Dans les limites autorisées par le droit applicable, Innovactive360° décline toute
                responsabilité pour :
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Les pertes de données résultant d'une utilisation incorrecte de la Plateforme.</li>
                <li>Les préjudices indirects, accessoires ou consécutifs (manque à gagner, perte d'opportunité, etc.).</li>
                <li>Le contenu publié par les utilisateurs ou les instructeurs tiers.</li>
                <li>Les liens hypertextes vers des sites tiers accessibles depuis la Plateforme.</li>
                <li>Les résultats obtenus suite à l'utilisation de l'assistant IA, fourni à titre informatif uniquement.</li>
              </ul>
              <p>
                La responsabilité totale d'Innovactive360° à votre égard ne peut excéder le montant
                des sommes que vous avez effectivement versées au cours des 12 derniers mois
                précédant le fait générateur du dommage.
              </p>
            </Section>

            <Section id="resiliation" icon={AlertTriangle} title="10. Résiliation du compte">
              <h3 className="font-semibold text-gray-700 mt-2">10.1 Résiliation par l'utilisateur</h3>
              <p>
                Vous pouvez supprimer votre compte à tout moment depuis les paramètres de votre profil
                ou en contactant <a href="mailto:support@innovactive360.com" className="text-primary hover:underline">support@innovactive360.com</a>.
                La suppression entraîne la désactivation immédiate de l'accès et la suppression
                progressive de vos données personnelles selon notre{' '}
                <a href="/privacy" className="text-primary hover:underline">Politique de confidentialité</a>.
              </p>
              <h3 className="font-semibold text-gray-700 mt-4">10.2 Résiliation par Innovactive360°</h3>
              <p>
                Innovactive360° peut suspendre ou résilier votre compte avec effet immédiat, sans
                préavis ni indemnité, en cas de :
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Violation des présentes Conditions.</li>
                <li>Comportement frauduleux ou abusif.</li>
                <li>Non-paiement des sommes dues.</li>
                <li>Décision judiciaire ou administrative.</li>
              </ul>
              <p>
                En cas de résiliation pour faute, aucun remboursement ne sera effectué pour la
                période d'abonnement en cours.
              </p>
            </Section>

            <Section id="modifications" icon={FileText} title="11. Modifications des conditions">
              <p>
                Innovactive360° se réserve le droit de modifier les présentes Conditions à tout
                moment. Toute modification substantielle vous sera notifiée par e-mail et/ou par
                une notification sur la Plateforme au moins <strong>30 jours avant</strong> son entrée
                en vigueur.
              </p>
              <p>
                Votre poursuite de l'utilisation de la Plateforme après l'entrée en vigueur des
                nouvelles Conditions vaut acceptation de celles-ci. Si vous n'acceptez pas les
                nouvelles Conditions, vous devez cesser d'utiliser la Plateforme et supprimer votre
                compte.
              </p>
            </Section>

            <Section id="droit" icon={Shield} title="12. Droit applicable et litiges">
              <p>
                Les présentes Conditions sont régies par le droit béninois. En cas de litige,
                les parties s'engagent à rechercher une solution amiable dans un délai de 30 jours.
              </p>
              <p>
                À défaut de résolution amiable, tout litige sera soumis à la compétence exclusive
                des juridictions compétentes de Parakou, Bénin, sauf disposition légale impérative contraire.
              </p>
              <p>
                Pour les utilisateurs résidant dans l'Union Européenne, les droits légaux dont vous
                bénéficiez en vertu des lois de protection des consommateurs de votre pays de
                résidence restent inchangés.
              </p>
            </Section>

            <Section id="contact" icon={Mail} title="13. Contact">
              <p>Pour toute question relative aux présentes Conditions, vous pouvez nous contacter :</p>
              <div className="bg-gray-50 rounded-lg p-4 mt-2 space-y-2">
                <p><strong>Innovactive360°</strong></p>
                <p>Parakou, Bénin</p>
                <p>
                  E-mail :{' '}
                  <a href="mailto:contact@innovactive360.com" className="text-primary hover:underline">
                    contact@innovactive360.com
                  </a>
                </p>
                <p>
                  Support :{' '}
                  <a href="mailto:support@innovactive360.com" className="text-primary hover:underline">
                    support@innovactive360.com
                  </a>
                </p>
                <p>Téléphone : (+229) 01 97 50 68 15</p>
              </div>
            </Section>

            <div className="text-center text-sm text-gray-400 mt-8 pb-4">
              Conditions d'utilisation — Innovactive360° — Version du {lastUpdate}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;
